import type { FC } from 'react';
import { useState } from 'react';
import { Text } from '@cencosud-cencommerce/paris-uikit';

import { useAppSelector } from '@/Application/Hooks/Redux';
import type {
  TBillingDeliveries,
  THomeDelivery,
  TIntangibleDelivery,
  TPickupDelivery
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import {
  Box,
  Card,
  CardHeader,
  Icon,
  Typography
} from '@/modules/perseo-billing/ui/react/src/components/beta';
import {
  isDeviceAndroid,
  isDeviceIos,
  isDeviceKiosco
} from '@/modules/perseo-billing/utils';
import { isWeddingFlow } from '@/modules/shared/utils/isWeddingFlow';

import { useTransformCartToDeliveries } from './cart-mapper-to-deliveries';
import { EditLink } from './edit-link.component';
import {
  HomeDeliveryDetail,
  HomeDeliverySummary
} from './home-delivery.component';
import {
  IntangibleDeliveryDetail,
  IntangibleDeliverySummary
} from './intangible.component';
import {
  PickupDeliveryDetail,
  PickupDeliverySummary
} from './pickup.component';

type TShowDeliveriesProps = {
  deliveries: TBillingDeliveries;
};

type TExpandDeliveries = {
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
};

function hasDuplicateStores(pickups: TPickupDelivery[]): boolean {
  const storeSet = new Set();

  for (const delivery of pickups) {
    storeSet.add(delivery.pickupStoreName);
  }

  return storeSet.size > 1;
}

export const DeliveryTypes: FC = () => {
  const { data: cart } = useAppSelector((state) => state.cart);
  const deliveries = useTransformCartToDeliveries(cart);

  const isWebView = isDeviceAndroid() || isDeviceIos() || isDeviceKiosco();
  const isWedding = isWeddingFlow();

  if (!cart) return null;

  const showEditLink = !(isWebView || isWedding);

  return (
    <Box variant="summary">
      <Card border>
        <div
          className="co-flex co-flex-col co-p-16px co-gap-y-8px co-w-full co-bg-white"
          data-testid="paris-delivery-types"
        >
          <CardHeader
            pl={0}
            pr={0}
            pt={0}
            pb={0}
            title={
              <h4 className="co-font-semibold" tabIndex={0}>
                Tipo de entrega
              </h4>
            }
            action={showEditLink && <EditLink />}
          />
          <div>
            <ShowDeliveries deliveries={deliveries} />
          </div>
        </div>
      </Card>
    </Box>
  );
};

const ShowDeliveries: FC<TShowDeliveriesProps> = ({ deliveries }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const intangibles = deliveries?.intangible;
  const pickups = deliveries?.pickUp ?? [];
  const homeDeliveries = deliveries?.homeDelivery;

  const homeDeliveryQty = homeDeliveries?.length ?? 0;
  const pickupQty = pickups?.length ?? 0;
  const intangibleQty = intangibles?.length ?? 0;
  const totalDeliveries = homeDeliveryQty + pickupQty + intangibleQty;

  const canExpand = totalDeliveries >= 2;

  const hasDifferentStores = hasDuplicateStores(pickups);

  let globalIndex = 0;

  const isWedding = isWeddingFlow();

  if (isWedding) {
    return (
      <div className="co-p-8px co-rounded-[4px] co-bg-neutral-100">
        <Text
          weight={600}
          className="co-text-[14px] co-leading-[20px] co-text-[#585858]"
        >
          La pareja podrá elegir el tipo de entrega o recibir el equivalente en
          dinero.
        </Text>
      </div>
    );
  }

  return (
    <div className="co-flex co-h-auto co-flex-col co-gap-2">
      {isExpanded || !canExpand ? (
        <>
          {homeDeliveries?.map((homeDelivery: THomeDelivery, index: number) => {
            return (
              <HomeDeliveryDetail
                key={index}
                homeDelivery={homeDelivery}
                totalDeliveries={totalDeliveries}
                index={globalIndex++}
              />
            );
          })}
          {pickups?.map((pickup: TPickupDelivery, index: number) => {
            return (
              <PickupDeliveryDetail
                key={index}
                pickup={pickup}
                totalDeliveries={totalDeliveries}
                index={globalIndex++}
              />
            );
          })}
          {intangibles?.map(
            (intangible: TIntangibleDelivery, index: number) => {
              return (
                <IntangibleDeliveryDetail
                  key={index}
                  intangible={intangible}
                  totalDeliveries={totalDeliveries}
                  index={globalIndex++}
                />
              );
            }
          )}
        </>
      ) : (
        <>
          {homeDeliveries && homeDeliveryQty > 0 && (
            <HomeDeliverySummary
              homeDelivery={homeDeliveries[0]}
              quantity={homeDeliveryQty}
              totalDeliveries={totalDeliveries}
              isExpanded={isExpanded}
            />
          )}
          {pickupQty > 0 && (
            <PickupDeliverySummary
              pickup={pickups[0]}
              quantity={pickupQty}
              totalDeliveries={totalDeliveries}
              hasDifferentStores={hasDifferentStores}
              isExpanded={isExpanded}
            />
          )}
          {intangibles && intangibleQty > 0 && (
            <IntangibleDeliverySummary
              intangible={intangibles[0]}
              quantity={intangibleQty}
              totalDeliveries={totalDeliveries}
            />
          )}
        </>
      )}
      {canExpand && (
        <ExpandDeliveries
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
        />
      )}
    </div>
  );
};

const ExpandDeliveries: FC<TExpandDeliveries> = ({
  isExpanded,
  setIsExpanded
}) => {
  const icon = !isExpanded ? 'NewArrowUpIcon' : 'NewArrowDownIcon';
  return (
    <button
      className="co-flex co-justify-end co-py-2 co-cursor-pointer co-gap-2"
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <Typography color="primary-500" weight="semibold" decoration="underline">
        {isExpanded ? 'Mostrar menos' : 'Mostrar más detalles'}
      </Typography>
      <Icon className="co-fill-current co-text-pds-primary-500" icon={icon} />
    </button>
  );
};

export default DeliveryTypes;
